// The one line under an outbound message that says what happened to the email.
//
// It is a module of its own, and pure, for two reasons. The first is that the wording
// is the whole point of it (see below) and wording that matters should be testable
// without rendering React — this project has no component-test harness, so a decision
// left inside MessageThread.jsx is a decision nothing can check. The second is that
// the decision is not one line: there are four outcomes and only one of them is the
// happy path.
//
// WHAT WAS WRONG BEFORE. The thread rendered
//
//     message.sent_at ? `Delivered …` : (message.delivery?.detail ?? 'Not emailed')
//
// and `delivery` existed only on the 201 the composer had just received. So on any
// refetch — a refresh, a navigation back, a colleague opening the ticket — the
// fallback was reached, and the fallback is a FLAT DENIAL: "Not emailed". For two
// outcomes that denial is false. `sent_unrecorded` means the customer HAS the mail and
// the row failed to record it; `send_unconfirmed` means Graph never answered the send
// and it may well have gone. In both, an agent who reads "Not emailed" does the
// obvious, correct-looking thing — sends it again — and the customer gets the same
// reply twice, which is the failure this whole subsystem is built to avoid.
//
// So: "we do not know" is now its own outcome, it is persisted server-side as a
// `ticket_events` row and rebuilt into `delivery` on every read (src/services/
// tickets.js), and it renders as a WARNING rather than as muted grey — because the
// muted grey line is the one an agent skims past on the way to clicking send.

/**
 * The two statuses that mean "we cannot prove the customer did NOT get this."
 *
 * Mirrors `UNCONFIRMED_DELIVERY` in src/mail/dispatch.js. A status this list has never
 * heard of is treated as ordinary rather than as a warning: the server can grow the
 * vocabulary before this file knows about it, and inventing an alarm for a value we
 * cannot interpret is worse than showing the sentence the server sent.
 */
export const UNCONFIRMED_DELIVERY = ['sent_unrecorded', 'send_unconfirmed'];

export const isUnconfirmedDelivery = (status) => UNCONFIRMED_DELIVERY.includes(status);

/**
 * What to render beneath one message, or null when there is nothing to say.
 *
 * @param {object} message  A `messages[]` entry, optionally carrying `delivery`.
 * @param {(iso: string) => string} formatDateTime  Injected so this module stays free
 *   of the `Intl` formatter and can be asserted on exactly.
 * @returns {{tone: 'muted'|'warning', text: string}|null}
 */
export function deliveryLine(message, formatDateTime) {
  // Internal notes are never emailed, so "Not emailed" about one would be noise
  // dressed as a fault; inbound mail was not sent by us at all.
  if (message.is_internal_note || message.direction !== 'outbound') return null;

  // `sent_at` first, and it outranks everything. It is written only after Graph
  // returned (docs/API.md §3.5), so it is the one unambiguous answer available — and a
  // stale marker must never be able to cast doubt on a send that provably completed.
  //
  // BOTH PLACES IT ARRIVES. `addMessage` builds the message it returns INSIDE its
  // transaction, and the send happens after that transaction commits — so on the 201
  // `message.sent_at` is always null and the timestamp is on `delivery.sent_at`
  // instead. Reading only the column rendered "Not emailed" under a reply that had
  // just been emailed, on the most common path there is once mail is live, and the
  // agent's remedy for that sentence is to send it again. A refetch was fine, but
  // Detail.jsx appends the 201 and only reloads when the ticket's status changed.
  const sentAt = message.sent_at ?? message.delivery?.sent_at ?? null;
  if (sentAt) {
    // "SENT", NOT "DELIVERED", and the difference is the whole point of this file.
    //
    // `sent_at` is Graph's own `sentAt`, written only once `sendMail` returned
    // (src/mail/outbound.js). That proves the message left us and Microsoft accepted
    // it. It proves nothing about the customer's mailbox: the receiving server can
    // still reject it, quarantine it, file it as spam, or accept and silently discard
    // it. This line used to say "Delivered", which is a claim the system cannot
    // support — and an agent who reads "Delivered" and hears nothing back concludes
    // the customer is ignoring them, when the mail may never have arrived.
    //
    // The only evidence of NON-delivery we hold is a bounce (`mail_bounces`, fed by
    // DSN parsing in src/mail/ingest.js), and absence of a bounce is weak evidence at
    // best — DSNs are routinely dropped. Real per-recipient delivery status would mean
    // Exchange's Message Trace, which is a separate integration.
    return { tone: 'muted', text: `Sent ${formatDateTime(sentAt)}` };
  }

  const delivery = message.delivery ?? null;

  if (isUnconfirmedDelivery(delivery?.status)) {
    return {
      tone: 'warning',
      // The instruction leads. `detail` is a good sentence but it is a sentence, and
      // the agent is mid-decision; the four words that stop the duplicate go first.
      text: `Delivery unconfirmed. Do not resend. ${delivery.detail ?? ''}`.trim(),
    };
  }

  // A reason the server gave for a genuine failure ("… not on MAIL_SEND_ALLOWLIST"),
  // present on the 201 only. Still muted: nothing was sent, so sending again is the
  // right thing and the screen should not fight the agent about it.
  if (delivery?.detail) return { tone: 'muted', text: delivery.detail };

  return { tone: 'muted', text: 'Not emailed' };
}
